import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, X, Layers, Maximize2 } from "lucide-react";

// Swipeable property gallery combining photos + floorplans, with a fullscreen lightbox.
export const MediaGallery = ({ images = [], floorplans = [], title = "", isRent = false }) => {
  const media = [
    ...images.map((url) => ({ url, kind: "photo" })),
    ...floorplans.map((url) => ({ url, kind: "floorplan" })),
  ];
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const touchX = useRef(null);

  const count = media.length;
  const go = useCallback((dir) => setIndex((i) => (i + dir + count) % count), [count]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") setLightbox(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, go]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  if (count === 0) return null;
  const current = media[index];

  return (
    <div data-testid="property-gallery">
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-3 h-[300px] md:h-[500px]">
        <div
          className="relative rounded-2xl overflow-hidden group cursor-zoom-in select-none"
          onClick={() => setLightbox(true)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="gallery-main"
        >
          <img src={current.url} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
          <span className="absolute top-5 left-5 px-4 py-2 text-[12px] font-500 tracking-[0.12em] uppercase text-white bg-[#b8935f] rounded-full">
            {isRent ? "To Let" : "For Sale"}
          </span>
          {current.kind === "floorplan" && (
            <span className="absolute top-5 right-5 px-3 py-1.5 text-[11px] font-500 uppercase text-[#2b2b2b] bg-white/90 rounded-full flex items-center gap-1.5"><Layers size={13} /> Floorplan</span>
          )}
          {count > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous" data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 hover:bg-white flex items-center justify-center text-[#2b2b2b] opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shadow-md"><ChevronLeft size={20} /></button>
              <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next" data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 hover:bg-white flex items-center justify-center text-[#2b2b2b] opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shadow-md"><ChevronRight size={20} /></button>
            </>
          )}
          <span className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/55 text-white text-[12px] flex items-center gap-1.5"><Maximize2 size={13} /> {index + 1} / {count}</span>
        </div>
        <div className="hidden lg:grid grid-rows-2 gap-3">
          {media.slice(0, 4).filter((_, i) => i !== index).slice(0, 2).map((m, i) => (
            <div key={i} className="rounded-2xl overflow-hidden cursor-pointer" onClick={() => setLightbox(true)}>
              <img src={m.url} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 mt-3 overflow-x-auto no-scrollbar" data-testid="gallery-thumbs">
        {media.map((m, i) => (
          <button key={i} onClick={() => setIndex(i)} className={`relative flex-shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 transition ${index === i ? "border-[#b8935f]" : "border-transparent opacity-70 hover:opacity-100"}`}>
            <img src={m.url} alt="" className="w-full h-full object-cover" />
            {m.kind === "floorplan" && <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[9px] text-center py-0.5">PLAN</span>}
          </button>
        ))}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/92 flex items-center justify-center" onClick={() => setLightbox(false)} data-testid="gallery-lightbox">
          <button onClick={() => setLightbox(false)} aria-label="Close" className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"><X size={22} /></button>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous" className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"><ChevronLeft size={24} /></button>
          <img src={current.url} alt={title} className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} />
          <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next" className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"><ChevronRight size={24} /></button>
          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-white/10 text-white text-[13px]">{current.kind === "floorplan" ? "Floorplan · " : ""}{index + 1} / {count}</span>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
